import { ComponentPropsWithoutRef, forwardRef } from 'react';

import classNames from 'classnames';

export const VARIANTS = ['solid', 'outlined', 'link'] as const;
export const SIZES = ['sm', 'md', 'lg', 'xl'] as const;
export const COLORS = [
  'paper',
  'black',
  'cyan',
  'blue',
  'magenta',
  'gray',
] as const;
export const WEIGHTS = ['normal', 'semibold'] as const;

// TODO remove extend to make intellisense sane
export interface ButtonProps extends ComponentPropsWithoutRef<'button'> {
  variant?: (typeof VARIANTS)[number];
  size?: (typeof SIZES)[number];
  color?: (typeof COLORS)[number];
  weight?: (typeof WEIGHTS)[number];
  circle?: boolean;
}

const sharedBaseStyles =
  'inline-flex items-center transition-colors disabled:opacity-75';

export function buttonClasses({
  variant = 'outlined',
  color = 'paper',
  size = 'md',
  weight = 'semibold',
  circle,
}: ButtonProps) {
  if (variant === 'link') {
    return '';
  }

  const styles = {
    // base
    [sharedBaseStyles]: true,
    'rounded-full border justify-center': true,

    // colors & variants
    'border-transparent': variant === 'solid',
    'border-black dark:border-gray-200':
      (color === 'paper' || color === 'black') && variant === 'outlined',
    'border-cyan-600': color === 'cyan' && variant === 'outlined',

    'bg-paper text-black hover:bg-gray-200 active:bg-gray-300':
      color === 'paper',
    'bg-black dark:bg-gray-100 text-paper dark:text-black hover:bg-gray-700 dark:hover:bg-gray-200 active:bg-gray-700 dark:active:bg-black':
      color === 'black',
    'bg-cyan-300 text-black hover:bg-cyan-400 active:bg-cyan-600':
      color === 'cyan',
    'bg-blue-500 text-paper hover:bg-blue-600 active:bg-blue-600':
      color === 'blue',
    'bg-magenta-500 text-paper hover:bg-magenta-600 active:bg-magenta-600':
      color === 'magenta',
    'bg-gray-100 text-black hover:bg-gray-200 active:bg-gray-300':
      color === 'gray',

    // sizes
    'text-body-300 min-h-button-sm min-w-button-sm gap-1': size === 'sm',
    'text-body-300 min-h-button-md min-w-button-md gap-1': size === 'md',
    'text-body-400 min-h-button-lg min-w-button-lg gap-1': size === 'lg',
    'text-body-400 min-h-button-xl min-w-button-xl gap-2': size === 'xl',

    // padding
    'px-2 py-px': size === 'sm' && !circle,
    'px-3 py-1': size === 'md' && !circle,
    'px-5 py-1': size === 'lg' && !circle,
    'px-8 py-2': size === 'xl' && !circle,

    // weight
    'font-600': weight === 'semibold',
    'font-400': weight === 'normal',

    // circle
    'p-0': circle,
  };

  return classNames(styles);
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = 'outlined',
      size = 'md',
      color = 'paper',
      circle = false,
      weight = 'semibold',
      ...props
    },
    ref,
  ) => {
    function linkStyles() {
      if (variant !== 'link') {
        return;
      }

      return {
        // base
        'hover:underline': true,

        // sizes
        'text-body-300': size === 'sm' || size === 'md',
        'text-body-400': size === 'lg' || size === 'xl',
      };
    }

    return (
      <button
        ref={ref}
        {...props}
        className={classNames(
          linkStyles(),
          buttonClasses({ variant, color, size, weight, circle }),
          props.className,
        )}
      >
        {children}
      </button>
    );
  },
);

Button.displayName = 'Button';
